import { useUpdateWithNestedListMutation } from 'store/services/flow/agendaRecords';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FieldErrors, FieldValues, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { useShoppingCart } from 'contexts';
import { FORM_ROLE_ITEMS, useFormRegistry } from 'contexts/FormRegistryProvider';
import type { RegisteredFormWithRole, Snapshots } from 'contexts/FormRegistryProvider';
import { VISIBILITY_STATE } from 'types/enums';
import type { FormItemWithoutVisibilities, RecordFormItem, RedirectType, SubmitMode, UpdateWithNestedListReq } from 'types';
import {
  getAttributeForSave,
  includeClickedButtonAttribute,
  getNestedListData,
  hasRecordsAttributeInput,
  getRecordsFromAttribute,
  setRecordsOnAttribute,
} from 'utils/agendaRecord/agendaRecord';
import { getAgendaRecordPath } from 'utils/agendaRecord/record';
import { handleRedirectActions } from 'utils/agendaRecord/handleRedirectActions';
import { validateMessage } from 'utils/agendaRecord/validateMessage';
import { handleSaveError } from 'utils/agendaRecord/handleSaveError';
import { mergeRegistryAndSnapshots } from 'utils/agendaRecord/mergeNestedLists';
import { FormValidationException } from 'utils/error/FormValidationException';
import { useDialogAction } from './useDialogAction';

interface UseRecordSubmissionProps {
  formNamespace: string;
  agendaId: string;
  moduleId?: string;
  childId?: string;
  systemType?: string;
  recordId?: string;
  items: RecordFormItem[];
  isNew: boolean;
  mode: SubmitMode;
  form: UseFormReturn;
  onLoadingChange?: (loading: boolean) => void;
  onValidationError?: (errors: FieldErrors, nestedListForms?: RegisteredFormWithRole[], snapshots?: Snapshots) => void;
  resetForms: () => void;
  useSelectedRowsForRecords?: boolean;
  lockToken?: string | null;
  onBeforeNavigate?: () => void;
  onLockConflict?: () => void;
}

export const useRecordSubmission = ({
  formNamespace,
  agendaId,
  moduleId,
  childId,
  systemType,
  recordId,
  items,
  isNew,
  mode,
  form,
  onLoadingChange,
  onValidationError,
  resetForms,
  useSelectedRowsForRecords = false,
  lockToken,
  onBeforeNavigate,
  onLockConflict,
}: UseRecordSubmissionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { triggerAll, getMountedForms, getAllFormSnapshots, hasValidSnapshots } = useFormRegistry(formNamespace);

  const [updateWithNestedList, { isLoading }] = useUpdateWithNestedListMutation();
  const { triggerRefetch: refreshCart } = useShoppingCart();
  // Once a "new" record is created, its id must be reused — otherwise dialog
  // resubmits / repeated submits keep posting recordId=null and duplicate it.
  const createdRecordIdRef = useRef<string | null>(null);
  const submittingRef = useRef(false);

  const [checkDialogAction, dialogComponent] = useDialogAction(form);

  const checkFormsValidation = useCallback(async () => {
    const valid = await form.trigger();
    const validSubForms = await triggerAll();
    const validSnap = hasValidSnapshots();

    return valid && validSubForms && validSnap;
  }, [form, hasValidSnapshots, triggerAll]);

  const prepareActionPostData = useCallback(
    (recdId: string | null, values: FieldValues, clickedButtonId?: string) => {
      const nestedListForms = getMountedForms(FORM_ROLE_ITEMS);
      const snapshots = getAllFormSnapshots(FORM_ROLE_ITEMS);
      const mergedForms = mergeRegistryAndSnapshots(nestedListForms, snapshots);

      const attributes = items
        ?.filter(({ visibilityState }) => visibilityState !== VISIBILITY_STATE.HIDDEN)
        .map(({ formItem }: { formItem: FormItemWithoutVisibilities }) => {
          const type = formItem.definition.type;
          // Button clicks are one-shot signals: isClicked comes only from the
          // button that triggered this submit, never from form state.
          const value = type === 'Button' ? formItem.id === clickedButtonId : values[formItem.id];
          const attribute = getAttributeForSave(type, formItem.id, value, formItem.definition);

          if (useSelectedRowsForRecords && type === 'Records' && hasRecordsAttributeInput(attribute)) {
            const matchingForm = mergedForms.find((f) => f.data?.formItemId === formItem.id);
            if (matchingForm?.data?.selectedRows && matchingForm.data.totalRows) {
              const { selectedRows, totalRows } = matchingForm.data;
              const isPartialSelection = selectedRows.length > 0 && selectedRows.length < totalRows.length;

              if (isPartialSelection) {
                const records = getRecordsFromAttribute(attribute);
                if (records) {
                  const filteredRecords = records.filter((recId) => selectedRows.includes(recId));
                  setRecordsOnAttribute(attribute, filteredRecords);
                }
              }
            }
          }

          return attribute;
        });

      const nestedListData = getNestedListData(mergedForms);

      return {
        agendaId,
        ...(lockToken ? { lockToken } : {}),
        body: {
          recordId: recdId,
          attributes: includeClickedButtonAttribute(attributes, clickedButtonId),
          nestedListData: nestedListData.length > 0 ? nestedListData : undefined,
        },
      } satisfies UpdateWithNestedListReq;
    },
    [agendaId, getAllFormSnapshots, getMountedForms, items, useSelectedRowsForRecords, lockToken]
  );

  const update = useCallback(
    async (submitData: UpdateWithNestedListReq) => {
      const res = await updateWithNestedList(submitData).unwrap();

      resetForms();

      if (useSelectedRowsForRecords) {
        refreshCart();
      }

      return res;
    },
    [resetForms, updateWithNestedList, useSelectedRowsForRecords, refreshCart]
  );

  const submitAction = useCallback(
    async (values: FieldValues, showSuccessToast = true, clickedButtonId?: string): Promise<boolean | undefined> => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        onLoadingChange?.(true);

        const recId = null;
        const submitData = prepareActionPostData(recId, values, clickedButtonId);
        const res = await update(submitData);

        validateMessage(res);

        if (showSuccessToast) {
          toast.success(t('toast.action.completed'));
        }

        onBeforeNavigate?.();
        return handleRedirectActions(res, navigate);
      } catch (error) {
        handleSaveError(error, {
          onActionError: () => toast.error(t('toast.error.action')),
          onUnknownError: () => toast.error(t('toast.error.action')),
          onLockConflict,
        });
        throw error;
      } finally {
        submittingRef.current = false;
        onLoadingChange?.(false);
      }
    },
    [onLoadingChange, prepareActionPostData, update, t, navigate, onBeforeNavigate, onLockConflict]
  );

  const submitRecord = useCallback(
    async (values: FieldValues, redirectType: RedirectType, showSuccessToast = true, clickedButtonId?: string): Promise<boolean | undefined> => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        onLoadingChange?.(true);

        const backupValues = form.formState.defaultValues;

        // For a new record reuse the id returned by the first create — dialog
        // resubmits and repeated submits must update it, not create copies.
        let recId = isNew ? (createdRecordIdRef.current ?? null) : (recordId ?? null);
        let submitData = prepareActionPostData(recId, values, clickedButtonId);

        let res = await update(submitData);
        recId = res.mainRecord.recordId;
        if (isNew) createdRecordIdRef.current = recId;

        while (true) {
          const dialogPromise = checkDialogAction(res, backupValues);
          if (!dialogPromise) break;

          const dialogResult = await dialogPromise;

          if (dialogResult === null || dialogResult.behavior === 'Keep') {
            return false;
          }

          if (dialogResult.behavior === 'DiscardAndSave' || dialogResult.behavior === 'KeepAndSave') {
            submitData = prepareActionPostData(recId, form.getValues(), dialogResult.buttonId);
            res = await update(submitData);
            recId = res.mainRecord.recordId;
            if (isNew) createdRecordIdRef.current = recId;
          }
        }

        validateMessage(res);

        if (showSuccessToast) {
          toast.success(t(isNew ? 'toast.added.record' : 'toast.updated.record'));
        }

        const didRedirect = handleRedirectActions(res, navigate);
        const shouldNavigate = !didRedirect && redirectType !== 'none' && Boolean(moduleId && systemType);
        // Stay on edit (SaveRequired autosave / save-and-edit) must keep the lock; only release when leaving.
        const isLeavingEdit = didRedirect || (shouldNavigate && redirectType !== 'edit');

        if (isLeavingEdit) {
          onBeforeNavigate?.();
        }

        if (shouldNavigate && moduleId && systemType) {
          navigate(getAgendaRecordPath(redirectType, moduleId, systemType, agendaId, res.mainRecord.recordId, childId), { replace: true });
          return true;
        }

        return didRedirect;
      } catch (error) {
        handleSaveError(error, {
          onActionError: () => toast.error(t('toast.error.action')),
          onUnknownError: () => toast.error(t('toast.error.save')),
          onLockConflict,
        });
        throw error;
      } finally {
        submittingRef.current = false;
        onLoadingChange?.(false);
      }
    },
    [
      onLoadingChange,
      isNew,
      recordId,
      prepareActionPostData,
      update,
      checkDialogAction,
      t,
      navigate,
      moduleId,
      systemType,
      form,
      agendaId,
      childId,
      onBeforeNavigate,
      onLockConflict,
    ]
  );

  const submit = useCallback(
    async (values: FieldValues, redirectType?: RedirectType, showSuccessToast = true, clickedButtonId?: string) => {
      if (mode === 'action') {
        return submitAction(values, showSuccessToast, clickedButtonId);
      } else {
        return submitRecord(values, redirectType || 'detail', showSuccessToast, clickedButtonId);
      }
    },
    [mode, submitAction, submitRecord]
  );

  const validateAllAndSubmit = useCallback(
    async (redirectType?: RedirectType, showSuccessToast = true, clickedButtonId?: string) => {
      const valid = await checkFormsValidation();
      if (!valid) {
        const nestedListForms = getMountedForms(FORM_ROLE_ITEMS);
        const snapshots = getAllFormSnapshots(FORM_ROLE_ITEMS);

        onValidationError?.(form.formState.errors, nestedListForms, snapshots);

        throw new FormValidationException('Invalid Form');
      }

      return submit(form.getValues(), redirectType, showSuccessToast, clickedButtonId);
    },
    [checkFormsValidation, form, getAllFormSnapshots, getMountedForms, onValidationError, submit]
  );

  const createWrappedHandler = useCallback(
    (handler: (values: FieldValues) => Promise<boolean | void>) => {
      return async (e?: React.FormEvent) => {
        e?.preventDefault();

        await form.handleSubmit(
          async () => {},
          async () => {}
        )(e);

        const valid = await checkFormsValidation();

        if (!valid) {
          const nestedListForms = getMountedForms(FORM_ROLE_ITEMS);
          const snapshots = getAllFormSnapshots(FORM_ROLE_ITEMS);

          onValidationError?.(form.formState.errors, nestedListForms, snapshots);
          return;
        }

        const formData = form.getValues();

        try {
          await handler(formData);
        } catch {
          // Error already handled by handleSaveError in submitAction/submitRecord
        }
      };
    },
    [checkFormsValidation, form, getMountedForms, getAllFormSnapshots, onValidationError]
  );

  const onSubmit = createWrappedHandler((values) => submit(values, 'detail'));
  const onSubmitAndClose = createWrappedHandler((values) => submit(values, 'close'));
  const onSubmitAndEdit = createWrappedHandler((values) => submit(values, 'edit'));

  return {
    submit,
    submitAction,
    submitRecord,

    onSubmit,
    onSubmitAndClose,
    onSubmitAndEdit,

    checkFormsValidation,
    validateAllAndSubmit,

    isLoading,
    getNestedListData,
    dialogComponent,
  };
};
